import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useLocale } from '@hooks/useLocale';
import Link from 'next/link';
import Skeleton from 'react-loading-skeleton';
import { BreadcrumbJsonLd } from 'next-seo';
import {
  BRANDS,
  MARQUES,
  COLLECTIONS,
  ASSORTIMENT,
} from '@utils/plp/constants';

import styles from './Breadcrumbs.module.scss';
import { getCategorySlugFromName } from '@utils/algolia/getCategorySlugFromName';
import { getCategorySlugsFromUrl } from '@utils/algolia/getCategorySlugsFromUrl';

export interface Breadcrumb {
  name: string;
  path: string;
}

export interface BreadcrumbsProps {
  breadcrumbCategories?: string[];
  productName?: string;
}

const Breadcrumbs = ({
  breadcrumbCategories = [],
  productName,
}: BreadcrumbsProps) => {
  const { t } = useTranslation('common');
  const { isFallback } = useRouter();
  const locale = useLocale();
  const generateLink = (item: Breadcrumb) => {
    const path = `/${locale}/${item.path}`;
    return <a href={path}>{item.name}</a>;
  };

  const dealWithSpecialCases = (breadcrumbs: Breadcrumb[]) => {
    // force brands slug to point to /brands
    let crumbs = breadcrumbs.map((crumb) => {
      if ([BRANDS, MARQUES].includes(crumb.name.toLocaleLowerCase())) {
        return {
          ...crumb,
          path: locale === 'en' ? BRANDS : MARQUES,
        };
      }
      return crumb;
    });

    // For custom collections, only return relevant breadcrumbs
    const currentPath = breadcrumbs.at(-1)?.path || '';
    const { categoryFilters } = getCategorySlugsFromUrl(currentPath, locale);
    const isCustomCollection = crumbs.some((crumb) =>
      [COLLECTIONS, ASSORTIMENT].includes(crumb.name.toLocaleLowerCase())
    );

    const numberOfBreadcrumbsToKeep = categoryFilters
      ? categoryFilters.length + 1
      : 1;
    if (isCustomCollection) {
      crumbs = [crumbs[0], ...crumbs.slice(-numberOfBreadcrumbsToKeep)];
    }

    return crumbs;
  };

  const breadcrumbsFromCategories = breadcrumbCategories
    .filter((category) => category !== 'all-products')
    .map((categoryName, index) => ({
      name: categoryName,
      path: breadcrumbCategories
        .slice(0, index + 1)
        .map((bcc) => {
          if (bcc === 'All') {
            return 'Products';
          }
          if (bcc === 'Tout') {
            return 'Produits';
          }
          return bcc;
        })
        .map((catName) => getCategorySlugFromName(catName, locale))
        .join('/'),
    }));

  const breadcrumbs = dealWithSpecialCases(breadcrumbsFromCategories);
  const baseUrl = `https://www.mec.ca/${locale}`;

  const jsonLdBreadcrumbs = [
    { name: t('breadcrumb.home'), path: '' },
    ...breadcrumbs,
  ].map((item, i) => ({
    position: i + 1,
    name: item.name,
    item: item.path ? `${baseUrl}/${item.path}` : baseUrl,
  }));

  return (
    <>
      {breadcrumbs && <BreadcrumbJsonLd itemListElements={jsonLdBreadcrumbs} />}
      <nav className={styles.container} aria-label={t('breadcrumb.aria')}>
        <ol className={styles.list}>
          <li className={styles.listItem}>
            <Link href={`/${locale}`}>
              {isFallback ? <Skeleton width={600} /> : t('breadcrumb.home')}
            </Link>
          </li>
          {breadcrumbs?.map((item, index) => (
            <li
              key={`breadcrumb-item-${item.name}`}
              className={styles.listItem}
            >
              <span>
                {!productName && index === breadcrumbs.length - 1
                  ? item.name
                  : generateLink(item)}
              </span>
            </li>
          ))}
          {productName && (
            <li key={`breadcrumb-item-productName`} className={styles.listItem}>
              <span className={styles.lastItem}> {productName} </span>
            </li>
          )}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;
